import localforage from 'localforage'

const DRAFT_VERSION = 1

const draftStore = localforage.createInstance({
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
  name: 'ckd-classic-report-draft',
})

const normalizeKeyPart = (value) =>
  encodeURIComponent(String(value || '-').trim() || '-')

export const getReportDraftKey = ({
  employeeCode,
  reportTemplateId,
  reportUuid,
  reportCode,
  tenantCode,
}) => {
  const reportKey = reportTemplateId
    ? `template-${reportTemplateId}`
    : reportUuid
      ? `uuid-${reportUuid}`
      : `new-${tenantCode || 'GLOBAL'}-${reportCode || 'report'}`

  return [
    'classic-report-draft',
    normalizeKeyPart(employeeCode || 'anonymous'),
    normalizeKeyPart(reportKey),
  ].join(':')
}

const cloneData = (value, fallback) => {
  try {
    return JSON.parse(JSON.stringify(value ?? fallback))
  } catch (error) {
    console.error('Cannot clone report draft data', error)
    return fallback
  }
}

const normalizeObject = (value, fallback = {}) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? cloneData(value, fallback)
    : fallback

const normalizeArray = (value) =>
  Array.isArray(value) ? cloneData(value, []) : []

export const normalizeReportDraftData = (reportData = {}) => ({
  templateData: normalizeObject(reportData?.templateData, {}),
  webviewData: normalizeObject(reportData?.webviewData, {}),
})

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'boolean') return value === true
  if (Array.isArray(value)) return value.some(hasMeaningfulValue)

  if (typeof value === 'object') {
    return Object.entries(value).some(([key, item]) => {
      if (['preview', 'openCollapse'].includes(key)) return false
      return hasMeaningfulValue(item)
    })
  }

  return false
}

export const hasReportDraftData = (reportData = {}) => {
  const data = normalizeReportDraftData(reportData)
  const webviewData = data.webviewData || {}
  const templateData = data.templateData || {}

  return Boolean(
    templateData?.name?.trim() ||
      templateData?.code?.trim() ||
      hasMeaningfulValue(templateData?.reportTemplateAliases) ||
      webviewData?.reportName?.trim() ||
      webviewData?.reportCode?.trim() ||
      hasMeaningfulValue(webviewData?.printData) ||
      hasMeaningfulValue(webviewData?.dataSourceData?.list) ||
      hasMeaningfulValue(webviewData?.paramsData?.inputs),
  )
}

export const buildReportDraftSummary = (reportData = {}) => {
  const data = normalizeReportDraftData(reportData)
  const templateData = data.templateData || {}
  const webviewData = data.webviewData || {}
  const summary = []
  const reportName = templateData.name || webviewData.reportName
  const reportCode = templateData.code || webviewData.reportCode
  const boxes = Object.keys(webviewData.printData || {}).length
  const dataSources = normalizeArray(webviewData.dataSourceData?.list).length
  const params = normalizeArray(webviewData.paramsData?.inputs).length

  if (reportName) summary.push(reportName)
  if (reportCode) summary.push(reportCode)
  if (boxes) summary.push(`${boxes} boxes`)
  if (dataSources) summary.push(`${dataSources} data sources`)
  if (params) summary.push(`${params} params`)

  return summary.join(' / ') || 'Classic Report'
}

export const getReportDraftFormData = (draft) =>
  normalizeReportDraftData(draft?.data)

export const getReportDraft = async (owner) => {
  try {
    return await draftStore.getItem(getReportDraftKey(owner))
  } catch (error) {
    console.error('Cannot read report draft', error)
    return null
  }
}

export const saveReportDraft = async (owner, reportData) => {
  const key = getReportDraftKey(owner)
  const data = normalizeReportDraftData(reportData)

  try {
    if (!hasReportDraftData(data)) {
      await draftStore.removeItem(key)
      return null
    }

    const draft = {
      version: DRAFT_VERSION,
      key,
      type: 'CLASSIC_REPORT',
      owner,
      data,
      summary: buildReportDraftSummary(data),
      updatedAt: new Date().toISOString(),
    }

    await draftStore.setItem(key, draft)
    return draft
  } catch (error) {
    console.error('Cannot save report draft', error)
    return null
  }
}

export const clearReportDraft = async (owner) => {
  try {
    await draftStore.removeItem(getReportDraftKey(owner))
  } catch (error) {
    console.error('Cannot clear report draft', error)
  }
}
